/* eslint-disable */
import Vue from "vue";
import Vuex from "vuex";

import VueResource from "vue-resource";
import { CONFIG } from "../config";
import { Helper } from "../helpers";
import { preselectConfiguratorItems } from "../helpers/specification/preselectConfiguratorItems";
import { addPercentage } from "../helpers/addPercentage";
import { findDeliveryTimeForAmount } from "../helpers/findDeliveryTimeForAmount";

Vue.use(Vuex);
Vue.use(VueResource);

export const EventBus = new Vue();

// Option groups/options tagged Hidden are never choosable by the customer (unlike Disabled,
// which is already filtered out server-side before reaching the configurator). They can still
// be selected behind the scenes (e.g. a default forced elsewhere), so they stay in the catalog
// data and are filtered out purely on the frontend, the same way as "locked from editor" groups.
const getHiddenOptionGroupIdsFromState = state => (
	((state.selectedFormat && state.selectedFormat.productOptionGroups) || [])
		.filter(g => g.isHidden)
		.map(g => g.id)
);

const getHiddenOptionIdsFromState = state => (
	((state.selectedFormat && state.selectedFormat.productOptionGroups) || [])
		.flatMap(g => g.productOptions || [])
		.filter(o => o.isHidden)
		.map(o => o.id)
);

// Options/groups scoped (via OnlyForCreationSources) to creation sources that don't include this
// upload's actual origin aren't valid for it - e.g. something scoped to only the configurator doesn't
// apply to an upload that came from the online editor. The backend lists those ids here so they get
// hidden the same way as Hidden-tagged options.
const getNonConfiguratorOptionIdsFromState = state => (
	state.config.configurationSettings.nonConfiguratorProductOptionIds || []
);

export const store = new Vuex.Store({
	state: {
		baseProps: {},
		productData: [],
		viewPort: {
			width: 0,
			s: false,
			ms: false,
			m: false,
			l: false,
			xl: false
		},
		config: {
			price: {
				PriceWithTax: null,
				PriceWithoutTax: null
			},
			extra: {},
			priceTable: [],
			offsetPriceTable: [],
			configurationSettings: {
				productGroupIdsForcedByEditor: [],
				fileIsUploaded: false,
				nonConfiguratorProductOptionIds: []
			},
			currentConfigSupportsOffset: false,
			printSidesCount: null
		},
		configurator: {
			steps: {
				current: {
					data: {},
					index: 0
				},
				all: [],
				available: {
					next: false,
					prev: false
				},
				first () {
					return this.all.first();
				},
				last () {
					return this.all.last();
				},
				general: {
					configuratorDataValid: false,
					optionsRenderable: false,
					existingConfigurationAdded: false,
					existingConfigurationValidatedSteps: false,
					existingConfigurationIsFinalized: false
				}
			},
			changed: false,
			configurationChanged: false,
			boundaries: null
		},
		configuratorData: {
			hasMultipleProducts: false,
			hasFormatMatches: false,
			hasFormatSuggestions: false,
			canSetConfigurationPrice: true
		},
		customPageCount: null,
		alteredPageCount: null,
		orderLine: {
			amount: 0,
			isRush: false,
			isOffset: false,
			offsetSkuId: null
		},
		order: {
			status: false,
			message: "",
			thumbnailUrl: null
		},
		file: {
			pageCount: null,
			content: null,
			cover: null,
			contentIsUploaded: false,
			coverIsUploaded: false,
			uploadsComplete: false,
			uploadSessionGuid: null,
			uploadSizes: {
				content: null,
				cover: null
			},
			customErrorMessages: {
				content: null,
				cover: null
			},
			isCustomSize: false,
			matchedCustomSizeFormat: null
		},
		customSize: {
			width: null,
			height: null
		},
		customSizeValid: false,
		isLoaded: false,
		selectedProduct: null,
		selectedFormat: { id: null },
		formatSuggestions: [],
		formatMatches: [],
		selectedOptions: [],
		selectedGroups: [],
		printSidesAvailability: {
			single: false,
			double: false,
			set: false
		},
		coverPrintSidesAvailability: {
			single: false,
			double: false,
			set: false
		},
		requiredOptionIds: {},
		requiredOptionsWithinGroups: {},
		availableOptions: [],
		unavailableOptions: [],
		excludedByFilters: [],
		type: "initial",
		throttle: {
			amount: null
		},
		loading: {
			price: false
		},
		quotationResponse: null,
		name: "",
		dictionary: {
			optionCategory: {
				0: "default",
				1: "paper",
				2: "print",
				3: "finish",
				4: "material"
			},
			chunkType: {
				0: "cover",
				1: "content"
			},
			orientation: {
				0: "both",
				1: "portrait",
				2: "landscape"
			}
		},
		svg: {
			editable: "<svg class=\"orientation__svg\" xmlns=\"http://www.w3.org/2000/svg\" x=\"0px\" y=\"0px\" viewBox=\"0 0 25 25\" enable-background=\"new 0 0 1000 1000\"><path d=\"M23.25 2.893L21.128.771c-.943-.944-2.592-.944-3.536 0L2.248 16.118l-.004.003-.003.004-.248.248a.499.499 0 0 0-.133.239L.343 23.077a.5.5 0 0 0 .601.601l6.466-1.517a.499.499 0 0 0 .239-.133l.248-.247.004-.003.003-.004L23.25 6.428a2.505 2.505 0 0 0 0-3.535zm-2.487 4.608L16.53 3.247l.708-.709 4.244 4.243-.719.72zM7.546 20.717l-4.242-4.242L15.823 3.954l4.233 4.254-12.51 12.509zm-4.824-3.409l3.991 3.99L1.5 22.521l1.222-5.213zm19.82-11.587l-.354.354-4.244-4.244.354-.354c.566-.566 1.555-.567 2.122 0L22.542 3.6a1.503 1.503 0 0 1 0 2.121z\"/></svg>",
			locked: "<svg class=\"orientation__svg\" xmlns=\"http://www.w3.org/2000/svg\" x=\"0px\" y=\"0px\" viewBox=\"0 0 1000 1000\" enable-background=\"new 0 0 1000 1000\"><path d=\"M321.8,455.5h356.4V321.8c0-49.2-17.4-91.2-52.2-126c-34.8-34.8-76.8-52.2-126-52.2c-49.2,0-91.2,17.4-126,52.2c-34.8,34.8-52.2,76.8-52.2,126L321.8,455.5L321.8,455.5z M900.9,522.3v400.9c0,18.6-6.5,34.3-19.5,47.3c-13,13-28.8,19.5-47.3,19.5H165.9c-18.6,0-34.3-6.5-47.3-19.5c-13-13-19.5-28.8-19.5-47.3V522.3c0-18.6,6.5-34.3,19.5-47.3c13-13,28.8-19.5,47.3-19.5h22.3V321.8c0-85.4,30.6-158.7,91.9-219.9C341.3,40.7,414.7,10,500,10c85.3,0,158.7,30.6,219.9,91.9c61.3,61.3,91.9,134.6,91.9,219.9v133.6h22.3c18.6,0,34.3,6.5,47.3,19.5C894.4,488,900.9,503.7,900.9,522.3L900.9,522.3z\"/></svg>",
			orientation: {
				landscape: "<svg class=\"orientation__svg\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 500 500\"><defs><style>.cls-1{fill:#7DC8FF;}</style></defs><rect class=\"cls-1\" x=\"13.95\" y=\"83.75\" width=\"472.1\" height=\"333.8\"/></svg>",
				portrait: "<svg class=\"orientation__svg\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 500 500\"><defs><style>.cls-1{fill:#7DC8FF;}.cls-2{fill:#7DC8FF;}</style></defs><rect class=\"cls-1\" x=\"167.1\" y=\"299.75\" width=\"165.8\" height=\"117.2\"/><rect class=\"cls-2\" x=\"83.1\" y=\"14.6\" width=\"333.8\" height=\"472.1\"/></svg>",
				both: "<svg class=\"orientation__svg\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 500 500\"><defs><style>.cls-3{fill:#E73467;stroke:#E73467;stroke-width:60;stroke-linejoin:round;stroke-miterlimit:10;}.cls-4{fill:none;stroke:#E73467;stroke-width:70;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:10;}</style></defs><polygon class=\"cls-3\" points=\"229.6,84.3 229.6,134.5 186.1,109.4 142.6,84.3 186.1,59.2 229.6,34.1\"/><polygon class=\"cls-3\" points=\"270.4,415.7 270.4,365.5 313.9,390.6 357.4,415.7 313.9,440.8 270.4,465.9\"/><path class=\"cls-4\" d=\"M85.5,229.6c-0.8,6.7-1.2,13.5-1.2,20.4c0,91.5,74.2,165.7,165.7,165.7c6.9,0,13.7-0.4,20.4-1.2\"/><path class=\"cls-4\" d=\"M414.5,270.4c0.8-6.7,1.2-13.5,1.2-20.4c0-91.5-74.2-165.7-165.7-165.7c-6.9,0-13.7,0.4-20.4,1.2\"/></svg>"
			},
			icon: {
				inkDrops: "<svg xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" class=\"svg svg--ink-drops\" viewBox=\"0 0 632.137 632.137\" xml:space=\"preserve\"><g><g><path d=\"M225.408,20.392c-6.525,0-12.653,3.13-16.486,8.401C200.795,39.967,10.063,303.782,10.063,416.792    c0,118.74,96.605,215.345,215.344,215.345c118.75,0,215.365-96.604,215.365-215.345c0-113.01-190.742-376.825-198.857-387.999    C238.071,23.521,231.943,20.392,225.408,20.392z\"/><path d=\"M526.284,8.401C522.44,3.13,516.303,0,509.777,0s-12.652,3.13-16.486,8.401c-15.997,22.013-95.789,134.584-95.789,189.203c0,61.909,50.356,112.275,112.275,112.275c61.909,0,112.296-50.367,112.296-112.275C622.063,142.985,542.271,30.414,526.284,8.401\"/></g></g></svg>",
				leaf: "<svg class=\"svg svg--leaf\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" stroke-width=\"2\"  fill=\"none\" stroke-linecap=\"round\" stroke-linejoin=\"round\"><path d=\"M5 21c.5 -4.5 2.5 -8 7 -10\" /> <path d=\"M9 18c6.218 0 10.5 -3.288 11 -12v-2h-4.014c-9 0 -11.986 4 -12 9c0 1 0 3 2 5h3z\" /></svg>"
			}
		}
	},
	getters: {
		checkData (state) {
			return {
				hasSelectedProduct: state.selectedProduct != null && state.selectedProduct.hasOwnProperty("specifications") && state.selectedProduct.id != null,
				hasSelectedFormat: state.selectedFormat.id != null,
				hasSelectedOptions: state.selectedOptions.length > 0
			};
		},
		getOrder (state) {
			return state.order;
		},
		getFile (state) {
			return state.file;
		},
		isConfiguratorDataValid (state) {
			return state.configurator.steps.general.configuratorDataValid;
		},
		isOptionsRenderable (state) {
			return state.configurator.steps.general.optionsRenderable;
		},
		isExistingConfigurationAdded (state) {
			return state.configurator.steps.general.existingConfigurationAdded;
		},
		getLoader (state) {
			return state.loading;
		},
		getConfiguratorData (state) {
			return state.configuratorData;
		},
		getConfiguratorChanged (state) {
			return state.configurator.configurationChanged;
		},
		getProductData (state) {
			return state.productData;
		},
		getAvailableFormats (state) {
			if (store.getters.checkData.hasSelectedProduct) {
				const getDimensions = specification => {
					return specification.productSize.widthInMm * specification.productSize.heightInMm;
				};

				return state.selectedProduct.specifications
					.sort((a, b) => {
						const isCustom = [a, b].some(s => s.isCustomSize);

						if (isCustom) {
							return a.isCustomSize ? 1 : -1;
						}

						const [aName, bName] = [a, b].map(s => s.productSize.name || "");
						const [aSize, bSize] = [a, b].map(s => getDimensions(s));

						if ([aName, bName].some(name => /^[A-z] ?[0-9]/i.test(name))) {
							if (aName[0] > bName[0]) {
								return -1;
							}

							if (aName[0] < bName[0]) {
								return 1;
							}
						}

						if (aSize > bSize) {
							return 1;
						}

						if (aSize < bSize) {
							return -1;
						}

						return 0;
					});
			}
		},
		getAvailableOptions (state) {
			return state.availableOptions;
		},
		getUnavailableOptions (state) {
			return state.unavailableOptions || [];
		},
		getExcludedByFilters (state) {
			return state.excludedByFilters || [];
		},
		getAllUnavailableOptions (state) {
			// Combine unavailable options (from requirements) with excluded options (from filters)
			return [...new Set([...(state.unavailableOptions || []), ...(state.excludedByFilters || []), ...(getHiddenOptionIdsFromState(state)), ...(getNonConfiguratorOptionIdsFromState(state))])];
		},
		getSelectedProduct (state) {
			return state.selectedProduct;
		},
		getSelectedFormat (state) {
			return state.selectedFormat;
		},
		bleedSize: (state) => (size) => {
			/**
			 * @returns Functions return trimmed and untrimmed bleedSize from size input
			 * @param {object} size to calculate bleed with
			 */
			const axisBleed = state.selectedFormat.bleed * 2;

			return {
				untrimmed: () => {
					return {
						height: size.height + axisBleed,
						width: size.width + axisBleed
					};
				},
				trimmed: () => {
					return {
						height: size.height - axisBleed,
						width: size.width - axisBleed
					};
				}
			};
		},
		getSelectedFormatDescription (state, getters) {
			/**
			 * @returns boolean if properties exist
			 * @param {object} size to check properties of
			 */
			const size = getters.getProductSize;

			if (size.width === 0 && size.height === 0) {
				if (state.selectedFormat.productSize.description) {
					return state.selectedFormat.productSize.description;
				} else if (state.selectedFormat.productSize.fullName) {
					return state.selectedFormat.productSize.fullName;
				} else {
					return null;
				}
			}

			const bleedSize = getters.bleedSize(size);
			let description = "";

			const trimmedSizeDescription = ({width, height}) => {
				return (
					`<strong>Eindformaat (excl. afloop): </strong>
					${width}mm breed x ${height}mm hoog.`
				);
			};

			const pdfSizeDescription = ({width, height}) => {
				return (
					`<strong>PDF-formaat (incl. afloop): </strong>
					${width}mm breed x ${height}mm hoog.`
				);
			};

			if (state.file.uploadsComplete) {
				if (state.selectedFormat.bleed) {
					description += trimmedSizeDescription(bleedSize.trimmed());
					description += "<br/>";
				}

				description += pdfSizeDescription(size);
			} else {
				description += trimmedSizeDescription(size);

				if (state.selectedFormat.bleed) {
					description += "<br/>";
					description += pdfSizeDescription(bleedSize.untrimmed());
				}
			}

			return description;
		},
		getSelectedOptions (state) {
			return Helper.sortProperties(state.selectedOptions, "index", true, false, true);
		},
		getUnsortedSelectedOptions (state) {
			return state.selectedOptions;
		},
		showOrientation (state) {
			return store.getters.getUniqueOrientations.includes(0) || store.getters.getUniqueOrientations.length > 1;
		},
		getUniqueOrientations (state) {
			const result = [];
			if (store.getters.getConfiguratorLoaded) {
				store.getters.getAvailableFormats.map(f => {
					if (!result.includes(f.productSize.orientation)) {
						result.push(f.productSize.orientation);
					}
				});
			}
			return result;
		},
		getGroupIndexedSelectedOptions () {
			const selectedOptions = store.getters.getSelectedOptions;
			const groupIndexedSelectedOptions = {};

			for (const option in selectedOptions) {
				groupIndexedSelectedOptions[selectedOptions[option].parentGroupId] = selectedOptions[option];
			}

			return groupIndexedSelectedOptions;
		},
		getSelectedGroups (state) {
			return Helper.sortProperties(state.selectedGroups, "index", true, false, true);
		},
		getUnsortedSelectedGroups (state) {
			return state.selectedGroups;
		},
		getSelectedOptionIds () {
			const ids = [];

			for (const option in store.getters.getSelectedOptions) {
				ids.push(store.getters.getSelectedOptions[option].id);
			}

			return ids;
		},
		getFormatSuggestions (state) {
			const formatSuggestions = state.formatSuggestions.reduce((suggestions, suggestion) => {
				const formatSuggestionData = state.selectedProduct.specifications
					.find(f => f.id === suggestion.specificationId);

				for (let attr in suggestion) {
					formatSuggestionData[attr] = suggestion[attr];
				}

				if (!formatSuggestionData.isMatch) {
					// check if it is a true suggestion instead of a match
					suggestions.push(formatSuggestionData);
				}

				return suggestions;
			}, []);

			return formatSuggestions;
		},
		getFormatMatches (state) {
			return state.formatMatches.sort((a, b) => a.id - b.id);
		},
		getCustomSize (state) {
			return state.customSize;
		},
		isCustomSize (state) {
			return state.file.isCustomSize;
		},
		getCustomSizeValid (state) {
			return state.customSizeValid;
		},
		getPageCount (state) {
			return state.alteredPageCount || state.file.pageCount || state.customPageCount;
		},
		getPrintSidesCount (state) {
			return state.config.printSidesCount;
		},
		getAllPageCounts (state) {
			return {
				custom: state.customPageCount,
				file: state.file.pageCount,
				altered: state.alteredPageCount
			};
		},
		getPrintSidesAvailability (state) {
			return state.printSidesAvailability;
		},
		getCoverPrintSidesAvailability (state) {
			return state.coverPrintSidesAvailability;
		},
		getDictionary (state) {
			return state.dictionary;
		},
		getSvg (state) {
			return state.svg;
		},
		isPrintSidesCountChoice (state) {
			if (store.getters.checkData.hasSelectedFormat) {
				return !state.baseProps.hasSeparateCover
					&& state.selectedFormat.minPageCount === 1
					&& state.selectedFormat.maxPageCount === 2
					&& (state.selectedFormat.pageIncrement === 1 || state.selectedFormat.pageIncrement === 0);
			}
		},
		currentConfigSupportsOffset (state) {
			return state.config.currentConfigSupportsOffset;
		},
		hasConfiguratorPageCountOptions (state) {
			// If the offer has any option configured with a fixed page count (CONFIGURATOR_PAGE_COUNT),
			// the page count is driven by the selected option instead of the page count slider.
			if (!state.selectedFormat || !state.selectedFormat.productOptionGroups) {
				return false;
			}

			return state.selectedFormat.productOptionGroups.some(group =>
				group.productOptions.some(option => option.configuratorPageCount !== null && option.configuratorPageCount !== undefined)
			);
		},
		validatePageCount (state) {
			if (store.getters.checkData.hasSelectedFormat) {
				const pages = state.customPageCount;
				const min = state.selectedFormat.minPageCount;
				const max = state.selectedFormat.maxPageCount;
				const step = state.selectedFormat.pageIncrement;

				return !(pages < min || pages > max || pages % step !== 0);
			}
		},
		getCurrentStep (state) {
			return state.configurator.steps.current;
		},
		getAllSteps (state) {
			return state.configurator.steps.all;
		},
		getStepsAvailability (state) {
			return state.configurator.steps.available;
		},
		getConfiguratorType (state) {
			return {
				isInitial: state.type === "initial",
				isUploader: state.type === "upload",
				isCalculate: state.type === "calculate",
				isConfiguration: state.type === "configuration" || state.baseProps.configId !== "null"
			};
		},
		getConfiguratorLoaded (state) {
			return state.isLoaded;
		},
		getUploaderPosition (state) {
			const type = store.getters.getConfiguratorType;

			return {
				before: !type.isConfiguration
					&& type.isUploader,
				after: !type.isConfiguration
					&& (type.isCalculate || type.isInitial)
					&& store.getters.isConfiguratorDataValid
			};
		},
		getUploadHasError (state) {
			return state.file.customErrorMessages.content || state.file.customErrorMessages.cover;
		},
		getConfiguratorTypeString (state) {
			return state.type;
		},
		getPrice (state) {
			return state.config.price;
		},
		getPriceExtra (state) {
			return state.config.extra;
		},
		getPriceTable (state) {
			return state.config.priceTable
				.filter(a => a.amount >= state.selectedFormat.minAmount)
				.sort((a, b) => a.amount - b.amount);
		},
		getOffsetPriceTable (state) {
			return state.config.offsetPriceTable
				.filter(a => a.Amount > 0)
				.sort((a, b) => a.Amount - b.Amount);
		},
		isOffset (state) {
			return state.orderLine.isOffset;
		},
		isOffTheShelf (state) {
			if (!state.selectedProduct) {
				return false;
			}

			if (state.selectedProduct.selectedFormat) {
				return state.selectedProduct.selectedFormat.isOffTheShelf;
			}

			return state.selectedProduct.specifications.some(s => s.isOffTheShelf);
		},
		isEditorProject (state) {
			return state.baseProps.isEditorProject;
		},
		isBookProduct (state) {
			// overwrite for booklike (losbladig) product. All booklike products need this check. This one explicitly because product has no cover
			const isLooseLeaf = store.getters.checkData.hasSelectedProduct
				? state.selectedProduct.name.includes("Losbladig")
				: false;

			return state.baseProps.hasSeparateCover || isLooseLeaf;
		},
		hasSeparateCover (state) {
			return state.baseProps.hasSeparateCover;
		},
		// Hybrid off-the-shelf product: the content is delivered off-the-shelf, so the single uploaded
		// file is the cover. It's posted as the content upload; the PDF API validates and stores it as cover.
		onlyCoverRequired (state) {
			return !!state.selectedFormat.onlyCoverRequired;
		},
		hasDigitalAvailable () {
			if (store.getters.checkData.hasSelectedFormat) {
				return store.getters.getAmountData.amountList.length !== 1 && store.getters.getAmountData.amountList[0] !== 0;
			}
		},
		hasOffsetAvailable (state) {
			if (store.getters.checkData.hasSelectedFormat) {
				return state.baseProps.hasOffsetAvailable;
			}
		},
		getAmountData (state) {
			const amountData = {
				customAmountAllowed: false,
				amountList: []
			};
			if (store.getters.checkData.hasSelectedFormat) {
				amountData.customAmountAllowed = state.selectedProduct.isCustomAmountAllowed;

				state.selectedProduct.amountList.split(",").forEach(a => {
					amountData.amountList.push(parseInt(a));
				});
				amountData.minAmount = state.selectedFormat.minAmount;
				amountData.maxAmount = state.selectedFormat.maxAmount;
			}
			return amountData;
		},
		getConfigurationName (state) {
			return state.name;
		},
		getOrderLine (state) {
			return state.orderLine;
		},
		isRushOrderAvailable (state) {
			// Rush is only a meaningful choice if it's actually faster than standard for the
			// currently selected amount - a rush production time being configured for the product
			// isn't enough on its own (near a cutoff, rush can compute to the same date as
			// standard). Mirrors the comparison Cart/StepTwo.cs makes server-side.
			const standard = store.getters.getStandardDelivery;
			const rush = store.getters.getRushDelivery;

			return !!(rush && standard && rush.rawDate < standard.rawDate);
		},
		getDeliveryTimes (state) {
			if (!state.selectedFormat.deliveryTimes) {
				return state.selectedProduct.specifications[0].deliveryTimes;
			} else {
				return state.selectedFormat.deliveryTimes;
			}
		},
		getStandardDelivery (state) {
			return findDeliveryTimeForAmount(
				store.getters.getDeliveryTimes,
				state.orderLine.amount,
				false,
				false);
		},
		getRushDelivery (state) {
			return findDeliveryTimeForAmount(
				store.getters.getDeliveryTimes,
				state.orderLine.amount,
				true,
				false);
		},
		getOffsetDelivery (state) {
			return findDeliveryTimeForAmount(
				store.getters.getDeliveryTimes,
				state.orderLine.amount,
				false,
				true);
		},
		getOffsetRushDelivery (state) {
			return findDeliveryTimeForAmount(
				store.getters.getDeliveryTimes,
				state.orderLine.amount,
				true,
				true);
		},
		getProductSize (state) {
			if (store.state.selectedFormat.isCustomSize) {
				if (state.file.uploadsComplete) {
					return {
						width: state.file.content.uploadSize.width,
						height: state.file.content.uploadSize.height
					};
				}

				if (state.customSize.width && state.customSize.height) {
					return {
						width: state.customSize.width,
						height: state.customSize.height
					};
				}
			}

			return {
				width: 0,
				height: 0
			};
		},
		getTrimmedProductSize (state, getters) {
			/**
			 * @returns the product size without bleed. After an upload the product size is the PDF size, which includes bleed
			 */
			const size = getters.getProductSize;

			if (state.selectedFormat.isCustomSize && state.file.uploadsComplete) {
				return getters.bleedSize(size).trimmed();
			}

			return size;
		},
		getCalculationPayload (state) {
			if (store.getters.checkData.hasSelectedFormat && store.getters.checkData.hasSelectedProduct) {
				return {
					id: store.state.baseProps.configId !== "null" ? parseInt(store.state.baseProps.configId) : null,
					productFrontId: store.state.selectedProduct.id,
					specificationId: store.state.selectedFormat.id,
					productOptionIds: store.getters.getSelectedOptionIds.length ? store.getters.getSelectedOptionIds : [],
					pages: store.getters.getPageCount,
					widthInMm: Math.round(store.getters.getTrimmedProductSize.width),
					heightInMm: Math.round(store.getters.getTrimmedProductSize.height),
					uploadPublicId: state.file.uploadSessionGuid
				};
			}
		},
		getPostConfig (state) {
			let data;
			let postConfig = {};

			if (store.getters.checkData.hasSelectedFormat
				&& store.getters.checkData.hasSelectedProduct
				&& store.getters.checkData.hasSelectedOptions) {
				if (state.baseProps.isConfiguration && !state.baseProps.specificationId) {
					data = store.getters.getConfigurationSettings;
				} else {
					data = store.getters.getCalculationPayload;
				}

				postConfig.id = data.id;
				postConfig.pageCount = store.getters.getPageCount
					|| store.getters.getSelectedFormat.minPageCount;

				postConfig.customAmount = store.state.orderLine.amount !== 0
					? store.state.orderLine.amount
					: data.amount;

				if (data.widthInMm !== 0) {
					postConfig.customWidthInMm = data.widthInMm;
				}
				if (data.heightInMm !== 0) {
					postConfig.customHeightInMm = data.heightInMm;
				}

				postConfig.specificationId = data.specificationId;
				postConfig.productFrontId = data.productFrontId;
				postConfig.name = data.name
					? data.name
					: store.getters.getConfigurationName;

				postConfig.productOptionIds = store.getters.getSelectedOptionIds;

				postConfig.hasOffsetPrintingAmount = store.state.orderLine.isOffset || false;

				if (store.state.orderLine.isOffset && store.state.orderLine.offsetSkuId) {
					postConfig.offsetSkuId = store.state.orderLine.offsetSkuId;
				}

				postConfig.uploadPublicId = state.file.uploadSessionGuid !== null
					? state.file.uploadSessionGuid
					: null;

				if (state.baseProps.isConfiguration
					&& !state.baseProps.specificationId
					&& data.uploadPublicId !== null
					&& typeof data.uploadPublicId !== "undefined"
					&& data.uploadPublicId !== "") {
					postConfig.uploadPublicId = data.uploadPublicId;
				}

				if (state.baseProps.isConfiguration
					&& !state.baseProps.specificationId
					&& data.editorProjectId !== null
					&& typeof data.editorProjectId !== "undefined"
					&& data.editorProjectId !== "") {
					postConfig.editorProjectId = data.editorProjectId;
				}
			}
			return postConfig;
		},
		getConfigurationSettings (state) {
			return state.config.configurationSettings;
		},
		getNonEditableOptionOfferIds (state) {
			return state.config.configurationSettings.nonEditableOptionOfferIds || [];
		},
		getLockedOptionGroupIds (state) {
			const lockedOfferIds = state.config.configurationSettings.nonEditableOptionOfferIds || [];
			if (!lockedOfferIds.length || !state.selectedFormat || !state.selectedFormat.productOptionGroups) {
				return [];
			}
			return state.selectedFormat.productOptionGroups
				.filter(g => g.productOptions.some(po => lockedOfferIds.includes(po.id)))
				.map(g => g.id);
		},
		getHiddenOptionGroupIds (state) {
			return getHiddenOptionGroupIdsFromState(state);
		},
		getNonSelectableOptionGroupIds (state) {
			const forcedByEditor = (state.config.configurationSettings.productGroupIdsForcedByEditor || []);
			return [...new Set([...forcedByEditor, ...getHiddenOptionGroupIdsFromState(state)])];
		},
		getUploadSessionGuid (state) {
			return state.file.uploadSessionGuid;
		},
		uploadsAreComplete (state) {
			return state.file.uploadsComplete;
		},
		getCustomErrorMessages (state) {
			return state.file.customErrorMessages;
		},
		getMatchedCustomSizeFormat (state) {
			return state.file.matchedCustomSizeFormat;
		},
		getMatchedCustomSizeFormatData (state) {
			if (store.getters.checkData.hasSelectedProduct) {
				return state.selectedProduct.specifications.find(
					f => f.id === store.getters.getMatchedCustomSizeFormat
				);
			}
		},
		isCustomUpload (state) {
			return state.file.isCustomSize;
		},
		getPreviewUrl (state) {
			if (!state.file.uploadsComplete && !state.baseProps.isConfiguration) {
				return null;
			}
			if (state.order.thumbnailUrl) {
				return state.order.thumbnailUrl;
			}
			if (!state.file.cover && state.file.content) {
				return state.file.content.mainPreviewUrl;
			}
			if (state.file.cover) {
				return state.file.cover.mainPreviewUrl;
			}
			return null;
		},
		existingConfigIsFinalized (state) {
			return state.configurator.steps.general.existingConfigurationIsFinalized;
		},
		getQuotationResponse (state) {
			return state.quotationResponse;
		},
		getBoundingRect (state) {
			return state.configurator.boundaries;
		},
		getViewPort (state) {
			return state.viewPort;
		},
		getGroupIdFromOptionId: state => id => {
			if (state.selectedFormat) {
				let groupId = null;

				state.selectedFormat.productOptionGroups.forEach(group => {
					const groupContainsId = group.productOptions.some(po => po.id === id);
					if (groupContainsId) {
						groupId = group.id;
					}
				});

				return groupId;
			}
		},
		getRequiredOptionsWithinGroups (state) {
			return state.requiredOptionsWithinGroups;
		},
		getStepExists: state => stepToCheck => {
			const steps = store.getters.getAllSteps;
			const stepExists = step => ({
				"string": steps.some(a => a.name === step),
				"number": steps.some(a => a.groupId === step)
			})[typeof step];

			const result = stepExists(stepToCheck);

			return {
				bool: result,
				class: result ? "cursor-pointer" : ""
			};
		},
		isSpecificationConfiguration (state) {
			return !!state.baseProps.specificationId;
		}
	},
	mutations: {
		CONFIGURATOR_ADD_STEP (state, payload) {
			if (!state.configurator.steps.all.find(x => x.name === payload.option.name)) {
				if (payload.position !== undefined) {
					state.configurator.steps.all.splice(payload.position, 0, payload.option);
				} else {
					state.configurator.steps.all.push(payload.option);
				}
			}
		},
		CONFIGURATOR_REMOVE_STEP (state, payload) {
			const step = state.configurator.steps.all.find(x => x.name === payload);
			if (step) {
				state.configurator.steps.all.splice(state.configurator.steps.all.indexOf(step), 1);
			}
		},
		CONFIGURATOR_UPDATE_STEP (state, payload) {
			state.configurator.steps.all[payload.index][payload.property] = payload.value;
		},
		CONFIGURATOR_SET_ACTIVE_STEP (state, payload) {
			if (payload !== undefined) {
				state.configurator.steps.current.data = payload;
				state.configurator.steps.current.index = state.configurator.steps.all.indexOf(payload);

				if (["configurator-overview", "price-table"].includes(payload.name)) {
					store.dispatch("_getPriceTable");

					if (store.getters.hasOffsetAvailable) {
						store.dispatch("_getOffsetPriceTable");
					}
					state.configurator.changed = false;
				}

				if (store.getters.checkData.hasSelectedOptions) {
					store.commit("CHECK_OPTION_AVAILABILITY");
					store.commit("CHECK_FILTER_EXCLUSIONS");
				}

				if (store.getters.checkData.hasSelectedFormat && payload.groupId) {
					// gets item within group
					let options = store.getters.getSelectedFormat.productOptionGroups.find(
						g => g.id === payload.groupId
					).productOptions;

					// Get all unavailable options (both from requirements and from exclusion filters)
					const allUnavailable = store.getters.getAllUnavailableOptions;

					// checks if item within groups are within unavailable options. If so alter options to the available options
					if (allUnavailable.length > 0) {
						options = options.filter(o => !allUnavailable.includes(o.id));
					}

					const hasSingleItem = options.length === 1;

					if (hasSingleItem) {
						const option = options[0];
						option.parentGroupId = payload.groupId;
						store.commit("SET_SELECTED_OPTION", option);
					}
				}

				store.commit("CONFIGURATOR_STEP_AVAILABLE");
			}
		},
		CONFIGURATOR_STEP_AVAILABLE (state) {
			const maxSteps = state.configurator.steps.all.length - 1;
			const currentStep = state.configurator.steps.current.index;
			if (state.configurator.steps.all[currentStep]) {
				const currentStepValid = state.configurator.steps.all[currentStep].valid;

				store.commit("CHECK_CONFIGURATOR_DATA_VALID");
				state.configurator.steps.available.next = currentStep < maxSteps && currentStepValid;
				state.configurator.steps.available.prev = currentStep > 0;
			}
		},
		CONFIGURATOR_STEP_NEXT (state) {
			const maxSteps = state.configurator.steps.all.length - 1;
			const currentStep = state.configurator.steps.current.index;

			if (currentStep < maxSteps) {
				store.commit("CONFIGURATOR_SET_ACTIVE_STEP", state.configurator.steps.all[currentStep + 1]);
			}
		},
		CONFIGURATOR_STEP_PREV (state) {
			const currentStep = state.configurator.steps.current.index;

			if (currentStep > 0) {
				store.commit("CONFIGURATOR_SET_ACTIVE_STEP", state.configurator.steps.all[currentStep - 1]);
			}
		},
		SET_STEP_VALID (state, payload) {
			const index = payload != null ? payload : state.configurator.steps.current.index;
			if (state.configurator.steps.all[index]) {
				state.configurator.steps.all[index].valid = true;
			}
		},
		SET_STEP_INVALID (state, payload) {
			const index = payload != null ? payload : state.configurator.steps.current.index;

			state.configurator.steps.all[index].valid = false;
		},
		SET_PRODUCT_DATA (state, payload) {
			state.productData = payload;
		},
		SET_BASE_PROPS (state, payload) {
			state.baseProps = payload;
			state.baseProps.isEditorProject = typeof payload.editorProject !== "undefined"
				? payload.editorProject.toLowerCase() === "true"
				: false;
			state.baseProps.isFront = payload.productFrontId[0] !== "null";
			state.baseProps.isConfiguration = payload.configId !== "null";
			state.baseProps.productFrontId = payload.productFrontId.map(id => id !== "null" ? parseInt(id) : "null");
			state.baseProps.hasSeparateCover = payload.hasCover.toLowerCase() === "true";
		},
		SET_CONFIGURATOR_PRODUCT_DATA (state) {
			state.configuratorData.hasMultipleProducts = state.productData.length > 1;
			if (!state.configuratorData.hasMultipleProducts) {
				store.commit("SET_SELECTED_PRODUCT", state.productData[0]);
			}

			state.isLoaded = true;
		},
		SET_CONFIGURATOR_FORMAT_DATA (state) {
			state.configuratorData.hasMultiplePages = state.selectedFormat.maxPageCount !== state.selectedFormat.minPageCount;
			state.configurator.steps.general.optionsRenderable = false;
			state.orderLine.amount = !state.selectedProduct.isCustomAmountAllowed
				? store.getters.getAmountData.amountList[0]
				: state.selectedFormat.minAmount;
		},
		SET_SELECTED_PRODUCT (state, payload) {
			state.selectedProduct = payload;
			state.configurator.steps.general.optionsRenderable = false;

			store.commit("CLEAR_SELECTED_FORMAT");
			store.commit("CLEAR_SELECTED_OPTIONS");
			store.commit("CLEAR_CONFIGURATION_PRICE");
			store.commit("INVALIDATE_ALL_STEPS_AFTER", "select-product");
			store.commit("CONFIGURATOR_STEP_AVAILABLE");
		},
		CLEAR_SELECTED_FORMAT (state) {
			state.selectedFormat = { id: null };
			state.configurator.steps.general.optionsRenderable = false;

			store.commit("CLEAR_SELECTED_OPTIONS");
			store.commit("CLEAR_CONFIGURATION_PRICE");
		},
		RESET_ALL_FILE_UPLOADS (state) {
			state.file.pageCount = null;
			store.commit("SET_MAIN_PREVIEW_URL", null);

			store.commit("RESET_FILE_UPLOAD", "content");
			store.commit("RESET_FILE_UPLOAD", "cover");

			store.commit("RESET_FORMAT_MATCHES");
			store.commit("RESET_FORMAT_SUGGESTIONS");

			store.commit("RESET_MATCHED_CUSTOMSIZE_FORMAT_SUGGESTION");
			store.commit("RESET_CUSTOM_ERROR_MESSAGE", "content");
			store.commit("RESET_CUSTOM_ERROR_MESSAGE", "cover");

			EventBus.$emit("resetUploadInput");
		},
		SET_SELECTED_FORMAT (state, payload) {
			if (payload !== store.getters.getSelectedFormat.id) {
				if (store.getters.checkData.hasSelectedFormat && payload !== null && store.getters.getUploaderPosition.after) {
					store.commit("CHECK_CONFIGURATOR_DATA_VALID");
					store.commit("SET_STEP_VALID");
					EventBus.$emit("selectedFormatDoesNotMatch");
					store.commit("SET_STEP_ACTIVE", "select-format");
				}

				state.selectedFormat = payload !== null
					? state.selectedProduct.specifications.find(f => f.id === payload)
					: { id: null };

				store.commit("CLEAR_SELECTED_OPTIONS");

				state.customPageCount = null;

				store.commit("CLEAR_CONFIGURATION_PRICE");
				store.commit("INVALIDATE_ALL_STEPS_AFTER", "select-format");

				if (payload !== null) {
					store.commit("BOOK_SINGLE_DOUBLE_SIDED_PAGECOUNT_PRINTSIDE_CHECK");

					if (store.getters.checkData.hasSelectedFormat) {
						store.commit("SET_CONFIGURATOR_FORMAT_DATA");

						if (state.baseProps.isConfiguration) {
							state.configurator.steps.general.optionsRenderable = true;
						}

						state.baseProps.hasOffsetAvailable = store.state.selectedFormat.offsetPricings.length > 0;

						if (!state.baseProps.isConfiguration) {
							store.dispatch("_getConfigurationPrice");
						}

						if (store.getters.uploadsAreComplete) {
							if (state.selectedFormat.isCustomSize) {
								// The upload size includes bleed, the custom size is without bleed
								store.commit("SET_CUSTOM_SIZE", store.getters.bleedSize(store.getters.getFile.content.uploadSize).trimmed());
							} else {
								store.commit("SET_CUSTOM_SIZE", { width: null, height: null });
							}
						}

						if (state.baseProps.isConfiguration) {
							store.commit("SET_ALL_CONFIGURATION_OPTIONS");
						}

						if (state.baseProps.isConfiguration && state.config.configurationSettings.previewUrl) {
							store.commit("SET_MAIN_PREVIEW_URL", state.config.configurationSettings.previewUrl);
						}

						store.commit("GET_REQUIRED_OPTION_IDS");
						store.commit("OPTION_AVAILABILITY_INIT");
					}
				}
			} else {
				store.commit("BOOK_SINGLE_DOUBLE_SIDED_PAGECOUNT_PRINTSIDE_CHECK");
			}

			store.commit("CONFIGURATOR_STEP_AVAILABLE");
		},
		BOOK_SINGLE_DOUBLE_SIDED_PAGECOUNT_PRINTSIDE_CHECK (state) {
			// The uploaded file of a hybrid off-the-shelf product is the cover, so its page count says nothing about the content
			if (store.getters.uploadsAreComplete && store.getters.isBookProduct && !store.getters.onlyCoverRequired) {
				store.commit("SET_PRINTSIDE_AVAILABILITY", store.getters.getFile.content.pageCount);
			}

			// Mirrors the content check above: the cover may finish uploading before a format is
			// selected (SET_COVER_UPLOAD's own SET_COVER_PRINTSIDE_AVAILABILITY call would bail with
			// no selected format yet), so re-derive it here too once a format is actually selected.
			if (store.getters.uploadsAreComplete && store.getters.hasSeparateCover && !store.getters.onlyCoverRequired) {
				store.commit("SET_COVER_PRINTSIDE_AVAILABILITY", store.getters.getFile.cover.isSingleSidedCover);
			}
		},
		SET_FORMAT_MATCHES (state, payload) {
			state.formatMatches = payload;
			state.configuratorData.hasFormatMatches = true;
		},
		SET_FORMAT_SUGGESTIONS (state, payload) {
			if (typeof payload !== "undefined") {
				state.formatSuggestions = payload;
				state.configuratorData.hasFormatSuggestions = true;

				if (payload.length === 1 && payload[0].isMatch) {
					state.configuratorData.hasFormatSuggestions = false;
				}
			}
		},
		RESET_FORMAT_MATCHES (state) {
			state.formatMatches = [];
			state.configuratorData.hasFormatMatches = false;
		},
		RESET_FORMAT_SUGGESTIONS (state) {
			state.formatSuggestions = [];
			state.configuratorData.hasFormatSuggestions = false;
		},
		GET_REQUIRED_OPTION_IDS (state) {
			const requiredOptionsWithinGroups = {};
			if (state.selectedFormat.productOptionGroups) {
				state.selectedFormat.productOptionGroups.forEach(group => {
					const optionsWithRequirements = group.productOptions.filter(i => i.requiredProductOptionIds.length);

					optionsWithRequirements.forEach(option => {
						requiredOptionsWithinGroups[option.id] = {};
						let groupsWithIds = {};

						option.requiredProductOptionIds.forEach(o => {
							const group = store.getters.getGroupIdFromOptionId(o);

							groupsWithIds[group] = groupsWithIds[group] || [];
							groupsWithIds[group].push(o);

							state.requiredOptionIds[o] = group;
						});
						requiredOptionsWithinGroups[option.id] = groupsWithIds;
					});
				});
			}
			state.requiredOptionsWithinGroups = requiredOptionsWithinGroups;
		},
		INVALIDATE_ALL_STEPS_AFTER (state, payload) {
			const lastValidStepIndex = state.configurator.steps.all.indexOf(
				state.configurator.steps.all.find(step => step.name === payload)
			);
			for (let i = lastValidStepIndex + 1; i < state.configurator.steps.all.length; i++) {
				state.configurator.steps.all[i].valid = false;
			}
			// edge case: Separating the page count selection from the format step creates a situation
			// where the default value for the page count is already set and valid, but the next button stays disabled.
			// We force this step to essentially always be valid, because it is.
			const pageCountStep = state.configurator.steps.all.find(step => step.name === "page-count");
			if (pageCountStep) {
				pageCountStep.valid = true;
			}
		},
		REMOVE_ALL_STEPS_AFTER (state, payload) {
			const lastValidStepIndex = state.configurator.steps.all.indexOf(
				state.configurator.steps.all.find(step => step.name === payload)
			);
			for (let i = lastValidStepIndex + 1; i < state.configurator.steps.all.length; i++) {
				store.commit("CONFIGURATOR_REMOVE_STEP", state.configurator.steps.all[i].name);
			}
		},
		CLEAR_SELECTED_OPTION (state, payload) {
			const group = state.selectedGroups.find(a => a.id === payload);
			const option = state.selectedOptions.find(a => a.parentGroupId === payload);

			const optionItemIndex = state.selectedOptions.indexOf(option);

			if (group.length && option.length) {
				state.selectedOptions.splice(optionItemIndex, 1);
			}

			const step = state.configurator.steps.all.find(a => a.groupId === payload);

			const stepIndex = state.configurator.steps.all.indexOf(step);
			store.commit("SET_STEP_INVALID", stepIndex);

			store.commit("CONFIGURATOR_STEP_AVAILABLE");
		},
		SET_SELECTED_OPTION_EXTRAS (state, payload) {
			// DWN Product 'Tabbladen' has an option for the amount of tabs. This should correspond to the pagecount for singlesided as well as doublesided.
			// The DWN API sends webshop ids (groups 3386/3392, options 815-817); the SDW custom configurator maps back to the legacy ids (group 135, options 149-151).
			const tabGroupIds = [135, 3386, 3392];
			const tabCountByOptionId = {
				149: 10,
				150: 15,
				151: 5,
				815: 10,
				816: 15,
				817: 5
			};
			const selectedTabOption = state.selectedOptions.find(o => tabGroupIds.includes(o.parentGroupId));

			if (selectedTabOption) {
				const tabCount = tabCountByOptionId[selectedTabOption.baseOptionId];
				const printSidesCount = store.getters.getPrintSidesCount || 1;

				if (tabCount) {
					store.commit("SET_PAGE_COUNT", tabCount * printSidesCount);
				}
			}
		},
		SET_SELECTED_OPTION (state, payload) {
			const checkExistingOption = state.selectedOptions.find(a => a.parentGroupId === payload.parentGroupId);
			const groupData = state.selectedFormat.productOptionGroups.find(a => a.id === payload.parentGroupId);
			const hasExistingOption = checkExistingOption !== undefined;

			const itemIndex = state.selectedOptions.indexOf(checkExistingOption);

			state.selectedOptions.splice(
				hasExistingOption
					? itemIndex
					: groupData.index >= 0
						? groupData.index
						: state.selectedOptions.length,
				hasExistingOption ? 1 : 0,
				payload
			);

			store.commit("SET_STEP_VALID");

			if (groupData && state.dictionary.optionCategory[groupData.optionCategory] === "print" && store.getters.isPrintSidesCountChoice) {
				store.commit("SET_PAGE_COUNT", payload.printSideCount);
			}

			if (groupData && state.dictionary.optionCategory[groupData.optionCategory] === "print" && state.dictionary.chunkType[groupData.chunkType] === "content") {
				store.commit("SET_PRINT_SIDES_COUNT", payload.printSideCount);
			}

			if (payload.configuratorPageCount !== null && payload.configuratorPageCount !== undefined) {
				store.commit("SET_PAGE_COUNT", payload.configuratorPageCount);
			}

			store.commit("SET_SELECTED_OPTION_EXTRAS", payload);
			store.commit("CONFIGURATOR_STEP_AVAILABLE");
			store.commit("ADD_TO_SELECTED_GROUPS", payload.parentGroupId);
			store.commit("CHECK_OPTION_AVAILABILITY");
			store.commit("CHECK_FILTER_EXCLUSIONS");

			if (state.requiredOptionIds.hasOwnProperty(payload.id)) {
				store.commit("CHECK_CONFIGURATOR_DATA_VALID");
			}

			if (!state.baseProps.isConfiguration || state.configurator.steps.general.existingConfigurationAdded) {
				store.dispatch("_getConfigurationPrice");
			}

			if (state.configurator.steps.general.existingConfigurationIsFinalized) {
				state.configurator.configurationChanged = true;
			}

			state.configurator.changed = true;
			store.commit("CONFIGURATOR_STEP_AVAILABLE");
		},
		SET_ALL_CONFIGURATION_OPTIONS (state) {
			if (state.baseProps.isConfiguration) {
				const configurationOptionIds = state.config.configurationSettings.productOptionIds;

				const optionGroups = store.getters.getSelectedFormat.productOptionGroups.sort((a, b) => a.index - b.index);
				for (const optionGroup in optionGroups) {
					let optionToSelect = null;

					for (const option in configurationOptionIds) {
						if (typeof optionGroups[optionGroup] !== "object") {
							return;
						}
						const initialOptionToSelect = optionGroups[optionGroup].productOptions.find(o => o.id === parseInt(configurationOptionIds[option]));

						if (initialOptionToSelect) {
							optionToSelect = initialOptionToSelect.baseOptionId !== 123 || !state.baseProps.isEditorProject
								? initialOptionToSelect
								: optionGroups[optionGroup].productOptions.find(o => o.baseOptionId === 122);
						}

						if (optionToSelect !== null) {
							break;
						}
					}

					if (optionToSelect !== null) {
						optionToSelect.parentGroupId = optionGroups[optionGroup].id;
						store.commit("SET_SELECTED_OPTION", optionToSelect);
					}
				}
			}
		},
		SET_ORDER (state, payload) {
			state.order = payload;
		},
		OPTION_AVAILABILITY_INIT (state) {
			if (state.selectedFormat.productOptionGroups) {
				state.selectedFormat.productOptionGroups.forEach(group => {
					group.productOptions.forEach(option => {
						state.availableOptions.push(option.id);
					});
				});

				// Bug #7649: option numbering was messed up whilst switching formats and optionsRenderable was set true too early. (Please dont remove setTimeout() because this made sure execution was timed properly)
				if (!state.baseProps.isConfiguration) {
					setTimeout(() => {
						state.configurator.steps.general.optionsRenderable = true;
					}, 0);
				}
			}
		},
		OPTION_AVAILABILITY_MET (state, payload) {
			const addIndex = state.availableOptions.includes(payload);
			if (!addIndex) {
				state.availableOptions.push(payload);
			}

			const removeIndex = state.unavailableOptions.indexOf(payload);
			if (removeIndex > -1) {
				state.unavailableOptions.splice(removeIndex, 1);
			}
		},
		OPTION_AVAILABILITY_UNMET (state, payload) {
			const removeIndex = state.availableOptions.indexOf(payload);
			if (removeIndex > -1) {
				state.availableOptions.splice(removeIndex, 1);
			}

			const addIndex = state.unavailableOptions.includes(payload);
			if (!addIndex) {
				state.unavailableOptions.push(payload);
			}
		},
		CHECK_OPTION_AVAILABILITY (state) {
			Object.keys(state.requiredOptionsWithinGroups).forEach(optionKey => {
				const option = state.requiredOptionsWithinGroups[optionKey];
				optionKey = parseInt(optionKey);

				let optionGroupIdToReset = store.getters.getGroupIdFromOptionId(optionKey);

				const hasOptionWithRequirementSelected = state.selectedOptions.find(o => o.id === optionKey);

				let availabilityArray = [];

				Object.keys(option).forEach(groupKey => {
					const group = option[groupKey];
					groupKey = parseInt(groupKey);

					const selectedOptionBasedOnGroup = state.selectedOptions.find(o => o.parentGroupId === groupKey);

					if (selectedOptionBasedOnGroup) {
						if (group.includes(selectedOptionBasedOnGroup.id)) {
							availabilityArray.push(true);
						} else {
							// HAS UNMET REQUIREMENTS
							availabilityArray.push(false);
						}
					}
				});

				if (availabilityArray.every(i => i === true)) {
					store.commit("OPTION_AVAILABILITY_MET", optionKey);
				} else {
					store.commit("OPTION_AVAILABILITY_UNMET", optionKey);
					if (hasOptionWithRequirementSelected) {
						store.commit("RESET_OPTION_GROUP", optionGroupIdToReset);
					}
				}

				store.commit("CHECK_CONFIGURATOR_DATA_VALID");
			});
		},
		CHECK_FILTER_EXCLUSIONS (state) {
			if (!state.selectedFormat || !state.selectedFormat.productOptionGroups) {
				state.excludedByFilters.splice(0, state.excludedByFilters.length);
				return;
			}

			const groups = state.selectedFormat.productOptionGroups;
			const allProductOptions = groups.reduce((acc, g) => acc.concat(g.productOptions), []);
			const selectedIds = state.selectedOptions.map(s => s.id);

			const excludedOptionOfferIds = new Set();

			// Requirement filters are intentionally NOT applied here as a hard allow-list: two
			// options that each require something in the other's group can otherwise deadlock the
			// picker (selecting one locks the other group down to its requirement, which locks the
			// first group back down, with no option left to click to escape it). The server-side
			// picker (OptionSelectionFilterHelper.GetCompatibleOptionOffers) leaves requirements out
			// for the same reason; CHECK_OPTION_AVAILABILITY / RESET_OPTION_GROUP below enforces them
			// reactively instead, by resetting the dependent group rather than blocking the picker.

			// Reverse direction — a selected option directly excludes other options.
			state.selectedOptions.forEach(selected => {
				const option = allProductOptions.find(po => po.id === selected.id);
				if (option && option.excludedProductOptionIds) {
					option.excludedProductOptionIds.forEach(id => excludedOptionOfferIds.add(id));
				}
			});

			// Forward direction — a candidate whose own exclusions would invalidate an existing
			// selection is hidden so the user can't pick it.
			allProductOptions.forEach(candidate => {
				if (selectedIds.includes(candidate.id)) {
					return;
				}

				if (candidate.excludedProductOptionIds
					&& candidate.excludedProductOptionIds.some(id => selectedIds.includes(id))) {
					excludedOptionOfferIds.add(candidate.id);
				}
			});

			state.excludedByFilters.splice(0, state.excludedByFilters.length, ...excludedOptionOfferIds);
		},
		RESET_OPTION_GROUP (state, payload) {
			state.selectedOptions.forEach((option, index) => {
				if (option.parentGroupId === payload) {
					state.selectedOptions.splice(index, 1);
				}
			});

			state.selectedGroups.forEach((group, index) => {
				if (group.id === payload) {
					state.selectedOptions.splice(index, 1);
				}
			});

			const step = state.configurator.steps.all.find(a => a.groupId === payload);
			const stepIndex = state.configurator.steps.all.indexOf(step);

			store.commit("SET_STEP_INVALID", stepIndex);
		},
		SET_CUSTOM_SIZE (state, payload) {
			state.customSize = payload;

			if (!state.baseProps.isConfiguration) {
				store.commit("CONFIGURATOR_STEP_AVAILABLE");
				if (payload.width && payload.height) {
					store.dispatch("_getConfigurationPrice");
				}
			}
		},
		SET_CUSTOM_SIZE_VALID (state, payload) {
			state.customSizeValid = payload;
		},
		SET_PAGE_COUNT (state, payload) {
			if (Number.isInteger(payload)) {
				state.customPageCount = parseInt(payload);
				store.dispatch("_getConfigurationPrice");
				store.commit("SET_ALTERED_PAGE_COUNT");
			}
		},
		SET_PRINT_SIDES_COUNT (state, payload) {
			const newPrintSidesCount = parseInt(payload);
			const oldPrintSidesCount = state.config.printSidesCount;

			// When editing an existing configuration without a new file upload, the page count
			// stored in customPageCount must be adjusted manually because SET_ALTERED_PAGE_COUNT
			// only recalculates alteredPageCount when a file is present. Products where the page
			// count is the sides count itself are excluded: SET_SELECTED_OPTION already set
			// customPageCount to the selected option's printSideCount, so compensating here
			// would immediately undo that change.
			if (!store.getters.isPrintSidesCountChoice
				&& !state.file.pageCount
				&& state.customPageCount
				&& oldPrintSidesCount
				&& newPrintSidesCount !== oldPrintSidesCount) {
				if (oldPrintSidesCount === 2 && newPrintSidesCount === 1) {
					// dubbelzijdig → enkelzijdig: double the page count
					state.customPageCount = state.customPageCount * 2;
				} else if (oldPrintSidesCount === 1 && newPrintSidesCount === 2) {
					// enkelzijdig → dubbelzijdig: halve the page count
					state.customPageCount = Math.floor(state.customPageCount / 2);
				}
			}

			state.config.printSidesCount = newPrintSidesCount;
			store.commit("SET_ALTERED_PAGE_COUNT");
		},
		SET_ALTERED_PAGE_COUNT (state, payload) {
			const singleSidePrintFileUploaded = state.printSidesAvailability.set
				&& state.printSidesAvailability.single
				&& state.file.pageCount > 0
				&& state.config.printSidesCount === 1;

			// vlakliggend
			if (state.selectedFormat.productType >= 4 && state.selectedFormat.productType <= 6) {
				state.alteredPageCount = null;
			} else if (singleSidePrintFileUploaded) {
				state.alteredPageCount = state.file.pageCount * 2;
			} else {
				state.alteredPageCount = null;
			}
		},
		SET_CONFIGURATION_NAME (state, payload) {
			state.name = payload;
		},
		SET_CONFIGURATOR_TYPE (state, payload) {
			state.type = payload || "initial";

			const calculateStep = store.getters.getConfiguratorData.hasMultipleProducts
				? "select-product"
				: "select-format";

			if (!store.getters.isConfiguratorDataValid) {
				store.commit("CONFIGURATOR_REMOVE_STEP", "uploader");
			}

			const setType = {
				"upload" () {
					store.commit("SET_STEP_ACTIVE", "uploader");
				},

				"calculate" () {
					store.commit("RESET_FILE_UPLOAD", "content");
					store.commit("RESET_FILE_UPLOAD", "cover");
					EventBus.$emit("resetUploadInput");
					store.commit("RESET_FORMAT_MATCHES");
					store.commit("RESET_FORMAT_SUGGESTIONS");
					store.commit("SET_STEP_ACTIVE", calculateStep);
				}
			}[payload];

			if (typeof setType === "function") {
				setType();
			} else {
				store.commit("SET_STEP_ACTIVE", store.state.configurator.steps.first());
			}
		},
		SET_STEP_ACTIVE (state, payload) {
			if (state.configurator.steps.all.length > 0) {
				const activeStep = {
					"string": state.configurator.steps.all.find(a => a.name === payload),
					"number": state.configurator.steps.all.find(a => a.groupId === payload),
					"object": payload
				}[typeof payload];
				store.commit("CONFIGURATOR_SET_ACTIVE_STEP", activeStep);
			}
		},
		SET_ORDERLINE_AMOUNT (state, payload) {
			state.orderLine.amount = parseInt(payload);

			if (state.orderLine.isOffset) {
				store.commit("SET_CONFIGURATION_OFFSET_PRICE", parseInt(payload));
			} else {
				store.dispatch("_getConfigurationPrice");
			}
		},
		SET_RUSH_SELECTED (state, payload) {
			state.orderLine.isRush = payload;
		},
		SET_OFFSET_SELECTED (state, payload) {
			state.orderLine.isOffset = payload;
			if (!payload) {
				state.orderLine.offsetSkuId = null;
				store.dispatch("_getConfigurationPrice");
			} else {
				if (state.orderLine.amount) {
					store.commit("SET_CONFIGURATION_OFFSET_PRICE", state.orderLine.amount);
				}
			}
		},
		SET_OFFSET_SKU_ID (state, payload) {
			state.orderLine.offsetSkuId = payload;
		},
		SET_FIRST_OFFSET_AMOUNT_SELECTED (state) {
			if (!store.getters.getOffsetPriceTable.length) {
				return;
			}

			if (!store.getters.hasDigitalAvailable && store.getters.hasOffsetAvailable) {
				const amount = state.orderLine.amount || store.getters.getOffsetPriceTable[0].Amount;
				store.commit("SET_RUSH_SELECTED", false);
				store.commit("SET_OFFSET_SELECTED", true);
				store.commit("SET_ORDERLINE_AMOUNT", amount);
				store.commit("SET_CONFIGURATION_OFFSET_PRICE", amount);
			}
		},
		ADD_TO_SELECTED_GROUPS (state, payload) {
			const groupExists = state.selectedGroups.some(a => a.id === payload);

			if (!groupExists) {
				const parentGroupData = state.selectedFormat.productOptionGroups.find(a => a.id === payload);
				const parentGroup = {
					chunkType: parentGroupData.chunkType,
					id: parentGroupData.id,
					index: parentGroupData.index,
					name: parentGroupData.name,
					optionCategory: parentGroupData.optionCategory,
					supportsCombinedPrint: parentGroupData.supportsCombinedPrint,
					title: parentGroupData.title
				};

				state.selectedGroups.push(parentGroup);
			}
		},
		CLEAR_SELECTED_OPTIONS (state) {
			if (state.selectedGroups.length > 0) {
				state.selectedGroups.splice(0, state.selectedGroups.length);
				state.selectedOptions.splice(0, state.selectedOptions.length);
			}
			state.excludedByFilters.splice(0, state.excludedByFilters.length);
			store.commit("AUTOMATICALLY_SET_OPTIONS");
		},
		AUTOMATICALLY_SET_OPTIONS (state) {
			if (!state.selectedFormat || !state.selectedFormat.productOptionGroups) {
				return;
			}
			const shouldPreSelectedTheseOptionGroups = state.selectedFormat.productOptionGroups.filter(productOptionGroup => productOptionGroup.productOptions.length === 1);
			if (shouldPreSelectedTheseOptionGroups.length > 0) {
				shouldPreSelectedTheseOptionGroups.forEach(optionGroup => {
					let option = optionGroup.productOptions[0];
					option.parentGroupId = optionGroup.id;
					store.commit("SET_SELECTED_OPTION", option);
				});
			}
		},
		CHECK_CONFIGURATOR_DATA_VALID (state) {
			// Ensure excludedByFilters reflects the current selection set, then drop any
			// selection that lives in that set. Cascading resets converge within a few
			// iterations; the bound protects against pathological cycles.
			store.commit("CHECK_FILTER_EXCLUSIONS");
			for (let i = 0; i < 5; i++) {
				const excluded = new Set(state.excludedByFilters || []);
				const conflicts = state.selectedOptions.filter(o => excluded.has(o.id));

				if (conflicts.length === 0) {
					break;
				}

				const groupsToReset = [...new Set(conflicts.map(o => o.parentGroupId))];
				groupsToReset.forEach(id => store.commit("RESET_OPTION_GROUP", id));
				store.commit("CHECK_FILTER_EXCLUSIONS");
			}

			const hasInvalidSteps = state.configurator.steps.all.some(step => !step.valid);
			const preparatorySteps = ["select-product", "select-format"];
			const insufficientProgression = preparatorySteps.includes(state.configurator.steps.current.data.name);
			const finalizedExistingConfiguration = state.baseProps.isConfiguration && state.configurator.steps.general.existingConfigurationAdded;

			if (state.baseProps.isConfiguration && !state.configurator.steps.general.existingConfigurationIsFinalized && !state.configurator.steps.general.existingConfigurationValidatedSteps) {
				store.commit("SET_EACH_STEP_VALID");
			}

			const validate = current => {
				if (store.getters.checkData.hasSelectedFormat) {
					// Groups forced by the editor or tagged Hidden are never presented to the customer, so
					// neither the group nor any selection already made for it (behind the scenes) should
					// count towards "did the customer complete every step they could actually see".
					const nonSelectableGroupIds = store.getters.getNonSelectableOptionGroupIds;
					const countableOptionGroups = state.selectedFormat.productOptionGroups.filter(g => !nonSelectableGroupIds.includes(g.id));
					const countableSelectedOptions = state.selectedOptions.filter(o => !nonSelectableGroupIds.includes(o.parentGroupId));
					const allOptionsSelected = countableOptionGroups.length === countableSelectedOptions.length;

					if (allOptionsSelected) {
						if (!finalizedExistingConfiguration) {
							store.commit("FINALIZE_EXISTING_CONFIG");

							return true;
						}
					} else {
						return false;
					}
				}

				if (state.configurator.steps.current.index > 0 && !insufficientProgression) {
					return !hasInvalidSteps;
				}

				if (!insufficientProgression) {
					return true;
				}

				return current;
			};

			state.configurator.steps.general.configuratorDataValid = validate(state.configurator.steps.general.configuratorDataValid);
		},
		SET_CONFIGURATION_PRICE (state, payload) {
			state.config.price = payload;

			if (store.getters.isRushOrderAvailable) {
				const rushPriceWithoutTax = addPercentage(
					payload.PriceWithoutTax,
					state.selectedProduct.rushOrderCostPercentage
				);
				state.config.extra.rushPrice = rushPriceWithoutTax;
				state.config.price.rushPriceWithoutTax = rushPriceWithoutTax;

				state.config.price.rushPriceWithTax = addPercentage(
					payload.PriceWithTax,
					state.selectedProduct.rushOrderCostPercentage
				);
			}

			const singleData = {
				PriceWithoutTax: payload.PriceWithoutTax / state.orderLine.amount,
				PriceWithTax: payload.PriceWithTax / state.orderLine.amount
			};

			store.commit("SET_CONFIGURATION_PRICE_SINGLE", singleData);
		},
		CLEAR_CONFIGURATION_PRICE (state) {
			state.config.price = {
				PriceWithTax: null,
				PriceWithoutTax: null
			};
			store.commit("CLEAR_ORDERLINE");
		},
		CLEAR_ORDERLINE (state) {
			state.orderLine = {
				amount: 0,
				isRush: false,
				isOffset: false,
				offsetSkuId: null
			};
		},
		SET_CONFIGURATION_PRICE_SINGLE (state, payload) {
			state.config.extra.singlePrice = payload.PriceWithoutTax;
			state.config.extra.singlePriceWithTax = payload.PriceWithTax;

			if (store.getters.isRushOrderAvailable) {
				state.config.extra.singleRushPrice = payload.PriceWithoutTax * state.selectedProduct.rushOrderCostPercentage + payload.PriceWithoutTax;
				state.config.extra.singleRushPriceWithTax = payload.PriceWithTax * state.selectedProduct.rushOrderCostPercentage + payload.PriceWithTax;
			}
		},
		SET_CONFIGURATION_OFFSET_PRICE (state, payload) {
			const offsetPriceTable = store.state.config.offsetPriceTable;
			let offset = offsetPriceTable.find(line => line.Amount === payload);

			if (!offset && offsetPriceTable.length) {
				offset = offsetPriceTable.reduce((closest, line) =>
					Math.abs(line.Amount - payload) < Math.abs(closest.Amount - payload) ? line : closest);
			}

			if (offset) {
				const offsetPrice = offset.Price;

				state.config.price.PriceWithoutTax = offsetPrice;
				const pricePayload = {
					PriceWithoutTax: offsetPrice
				};

				store.commit("SET_CONFIGURATION_PRICE", pricePayload);
			}
		},
		SET_EXISTING_CONFIGURATION_DATA (state, payload) {
			if (state.baseProps.isConfiguration) {
				state.config.configurationSettings = payload;
				state.configuratorData.canSetConfigurationPrice = !payload.hasOffsetPrintingAmount;
				store.state.baseProps.productFrontId = payload.productFrontId;
			}
		},
		SET_EXISTING_CONFIGURATION_SELECTED (state) {
			if (state.baseProps.isConfiguration) {
				store.commit("SET_SELECTED_FORMAT", state.config.configurationSettings.specificationId);
			}
		},
		SET_PRICE_TABLE (state, payload) {
			state.config.priceTable = payload;
		},
		SET_OFFSET_PRICE_TABLE (state, payload) {
			state.config.offsetPriceTable = payload;
		},
		SET_EACH_STEP_VALID (state) {
			const configurationOptionIds = store.getters.getConfigurationSettings.productOptionIds;
			const configuratorSteps = state.configurator.steps.all;

			configuratorSteps.forEach(step => {
				if (!step.groupId) {
					step.valid = true;
				} else {
					const configurationIncludesGroup = configurationOptionIds.some(id => store.getters.getGroupIdFromOptionId(id) === step.groupId);
					step.valid = configurationIncludesGroup;
				}
			});

			const validOptionSteps = configuratorSteps.filter(step => step.groupId && step.valid);

			const allConfigurableStepsValid = validOptionSteps.length === configurationOptionIds.length;
			const invalidStep = configuratorSteps.find(step => !step.valid);

			if (allConfigurableStepsValid && invalidStep) {
				state.configurator.steps.general.existingConfigurationValidatedSteps = true;
				store.commit("SET_STEP_ACTIVE", invalidStep);
			}
		},
		SET_OFFSET_SUPPORT (state, payload) {
			state.config.currentConfigSupportsOffset = payload;
		},
		SET_OFFSET_PRICETABLE (state, payload) {
			state.config.offsetPriceTable = payload;
			EventBus.$emit("finishedOffsetPriceTableFill");
		},
		FINALIZE_EXISTING_CONFIG (state) {
			if (state.baseProps.isConfiguration) {
				store.commit("SET_CUSTOM_SIZE", {
					width: state.config.configurationSettings.widthInMm,
					height: state.config.configurationSettings.heightInMm
				});

				if (state.config.configurationSettings.hasOffsetPrintingAmount) {
					store.commit("SET_OFFSET_SELECTED", true);
				}

				store.commit("SET_ORDERLINE_AMOUNT", state.config.configurationSettings.amount);

				EventBus.$once("finishedOffsetPriceTableFill", () => {
					if (state.config.configurationSettings.hasOffsetPrintingAmount) {
						store.commit(
							"SET_CONFIGURATION_OFFSET_PRICE",
							state.config.configurationSettings.amount
						);
					}
				});

				store.commit("SET_PAGE_COUNT", state.config.configurationSettings.pages);

				// Reopening an existing configuration never re-runs the upload flow, so
				// state.file.pageCount (only ever set by SET_CONTENT_UPLOAD) would otherwise
				// stay null here. option-item.vue's isPrintSidesCountChoice lock compares the
				// uploaded file's page count against each option's printSideCount, so without
				// this the single/double sided lock silently no-ops for reopened configurations.
				// Only do this once a PDF has actually been uploaded - configurationSettings.pages
				// defaults to the format's minimum page count before that, and setting file.pageCount
				// from that default would lock the sides choice before the customer uploaded anything.
				if (state.config.configurationSettings.hasUpload) {
					state.file.pageCount = state.config.configurationSettings.pages;
				}

				// Same reasoning as above: this derives single/double-sided availability from
				// the page count, so it must not run against the pre-upload default page count.
				if (store.getters.isBookProduct && state.config.configurationSettings.hasUpload) {
					store.commit("SET_PRINTSIDE_AVAILABILITY", state.config.configurationSettings.pages);
				}

				state.configurator.steps.general.configuratorDataValid = true;
				state.configurator.steps.general.existingConfigurationAdded = true;
			}
		},
		SET_FILE_UPLOAD (state, payload) {
			state.file[payload.type] = payload;
			store.commit("RESET_FORMAT_SUGGESTIONS");
			store.commit("RESET_FORMAT_MATCHES");
			store.commit("RESET_CUSTOM_ERROR_MESSAGE", payload.type.toLowerCase());

			if (payload.displayError) {
				store.commit("SET_CUSTOM_ERROR_MESSAGE", payload);
			} else {
				store.commit("SET_" + payload.type.toUpperCase() + "_UPLOAD");
				state.file.uploadSizes[payload.type.toLowerCase()] = payload.uploadSize;
			}
		},
		RESET_FILE_UPLOAD (state, payload) {
			state.file[payload] = null;
			state.file[payload + "IsUploaded"] = false;

			if (state.file.content === null
				|| (state.baseProps.hasSeparateCover && !store.getters.onlyCoverRequired && state.file.cover === null)) {
				store.commit("SET_UPLOADS_COMPLETE", false);
				state.file.isCustomSize = false;
			}
		},
		SET_COVER_UPLOAD (state) {
			state.file.coverIsUploaded = true;

			if (store.getters.hasSeparateCover) {
				store.commit("SET_COVER_PRINTSIDE_AVAILABILITY", state.file.cover.isSingleSidedCover);
			}

			if (state.file.contentIsUploaded) {
				store.commit("SET_UPLOAD_SUCCESS");
			}
		},
		SET_CONTENT_UPLOAD (state) {
			state.file.contentIsUploaded = true;

			// For hybrid off-the-shelf products the uploaded file is the cover, the content page count stays as configured
			if (!store.getters.onlyCoverRequired) {
				state.file.pageCount = state.file.content.pageCount;
			}

			if (!state.baseProps.hasSeparateCover || store.getters.onlyCoverRequired || state.file.coverIsUploaded) {
				store.commit("SET_UPLOAD_SUCCESS");
			}
		},
		SET_PRINTSIDE_AVAILABILITY (state, payload) {
			if (store.getters.checkData.hasSelectedFormat) {
				let result = {
					single: false,
					double: false,
					set: true
				};
				const min = state.selectedFormat.minPageCount;
				const minSingle = min / 2;
				const max = state.selectedFormat.maxPageCount;
				const maxSingle = max / 2;

				// 10 niet mogelijk
				if (payload >= minSingle && payload < min) {
					// 12 alleen enkelzijdig mogelijk
					result.single = true;
				} else if (payload >= min && payload <= maxSingle) {
					// 24 enkelzijdig en dubbelzijdig mogelijk
					// 200 enkelzijdig en dubbelzijdig mogelijk
					result.single = true;
					result.double = true;
				} else if (payload > maxSingle && payload <= max) {
					// 400 alleen dubbelzijdig mogelijk. enkelzijdig niet mogelijk
					result.double = true;
				}
				// 402 niet mogelijk

				if (!result.single && !result.double && result.set) {
					state.configuratorData.canSetConfigurationPrice = false;
					store.commit("RESET_ALL_FILE_UPLOADS");

					if (store.getters.getUploaderPosition.before) {
						store.commit("CLEAR_SELECTED_FORMAT");
						store.commit("CHECK_CONFIGURATOR_DATA_VALID");
						store.commit("CONFIGURATOR_STEP_AVAILABLE");
					}

					store.commit("CLEAR_CONFIGURATION_PRICE");
					store.commit("SET_STEP_ACTIVE", "uploader");

					EventBus.$emit("configuratorModal", {
						title: "Ongeldig pagina-aantal geüpload",
						content: `<p>Er zijn ${payload} pagina's geupload. Dat is een ongeldig aantal pagina's voor dit product. <br>Voor enkelzijdige bedrukking is het minimum ${minSingle} pagina's en het maximum ${maxSingle} pagina's. <br>Dubbelzijdige bedrukking heeft een minimum van ${min} pagina's en maximaal ${max} pagina's. De uploadvelden zijn gereset zodat u opnieuw kunt uploaden.</p>`
					});

					state.configuratorData.canSetConfigurationPrice = true;
				}

				state.printSidesAvailability = result;
			}
		},
		SET_COVER_PRINTSIDE_AVAILABILITY (state, payload) {
			// payload is the IsSingleSidedCover flag the backend already determined at upload time
			// (from the offer's actually available cover print-side options) - unlike the content
			// version above, this is never re-derived from a min/max page-count range client-side,
			// since a cover spec's page count isn't reliably "the doubled value, halve it for single"
			// (e.g. a single-sided-only cover can have its page count stored as the real value already).
			if (payload == null) {
				return;
			}

			state.coverPrintSidesAvailability = {
				single: payload === true,
				double: payload === false,
				set: true
			};
		},
		SET_UPLOADS_COMPLETE (state, payload) {
			if (payload) {
				// Sorry, this exists because there is a race condition that is triggered when uploading a file and pressing next (cover and content)
				if (state.configurator.steps.last().name === "uploader") {
					setTimeout(() => {
						state.configurator.steps.current.data.name = "configurator-overview";
						state.configurator.steps.current.index = state.configurator.steps.all
							.findIndex(s => s.name === "configurator-overview");
					}, 250);
				}
			}
			state.file.uploadsComplete = payload;
		},
		SET_UPLOAD_SUCCESS (state) {
			const hasError = state.file.customErrorMessages.content !== null || state.file.customErrorMessages.cover !== null;
			const hasFormatSuggestions = state.file.content.suggestions !== null && state.file.content.suggestions.length > 0;
			const specificationIds = (state.file.content && state.file.content.specificationIds) || [];

			if (!hasError) {
				store.commit("SET_UPLOADS_COMPLETE", true);
				store.commit("SET_STEP_VALID");
			}
			store.commit("CONFIGURATOR_STEP_AVAILABLE");

			if (specificationIds.length === 1) {
				if (state.file.content) {
					state.file.isCustomSize = state.selectedProduct.specifications
						.find(f => f.id === specificationIds[0]).isCustomSize;
				}

				if (!state.file.isCustomSize) {
					store.commit("SET_SELECTED_FORMAT", specificationIds[0]);
				} else {
					if (store.getters.getUploaderPosition.before) {
						if (hasFormatSuggestions) {
							store.commit("SET_FORMAT_SUGGESTIONS", state.file.content.suggestions);
						}
						store.commit(
							"SET_MATCHED_CUSTOMSIZE_FORMAT_SUGGESTION",
							specificationIds[0]
						);
					} else {
						store.commit("SET_SELECTED_FORMAT", specificationIds[0]);
					}
				}
			} else if (specificationIds.length > 1) {
				store.commit("SET_SELECTED_FORMAT", null);

				let payload = [];
				specificationIds.forEach(function (x) {
					state.selectedProduct.specifications.forEach(s => {
						if (s.id === x) {
							payload.push(s);
						}
					});
				});
				store.commit("SET_FORMAT_MATCHES", payload);

				if (hasFormatSuggestions) {
					store.commit("SET_FORMAT_SUGGESTIONS", state.file.content.suggestions);
				}
			}

			store.commit("SET_ALTERED_PAGE_COUNT");

			if (store.getters.checkData.hasSelectedFormat) {
				store.dispatch("_getConfigurationPrice");
			}
		},
		SET_MATCHED_CUSTOMSIZE_FORMAT_SUGGESTION (state, payload) {
			state.file.matchedCustomSizeFormat = payload;
		},
		RESET_MATCHED_CUSTOMSIZE_FORMAT_SUGGESTION (state) {
			state.file.matchedCustomSizeFormat = null;
		},
		SET_UPLOAD_SESSION_GUID (state, payload) {
			state.file.uploadSessionGuid = payload;
		},
		SET_CUSTOM_ERROR_MESSAGE (state, payload) {
			state.file.customErrorMessages[payload.type] = payload.message;
			state.file.uploadSizes[payload.type.toLowerCase()] = payload.uploadSize;

			const specificationIds = (state.file.content && state.file.content.specificationIds) || [];
			if (specificationIds.length === 1) {
				store.commit("SET_UPLOAD_SUCCESS");
			}

			if (payload.suggestions !== null && payload.suggestions.length > 0) {
				store.commit("SET_FORMAT_SUGGESTIONS", payload.suggestions);
			}
		},
		SET_QUOTATION_RESPONSE (state, payload) {
			store.state.quotationResponse = payload;
		},
		RESET_CUSTOM_ERROR_MESSAGE (state, payload) {
			state.file.customErrorMessages[payload] = null;
		},
		SET_MAIN_PREVIEW_URL (state, payload) {
			state.order.thumbnailUrl = payload;
		},
		FINAL_STEP_EXISTING_CONFIG (state) {
			state.configurator.steps.general.existingConfigurationIsFinalized = true;

			if (!state.configuratorData.canSetConfigurationPrice) {
				EventBus.$once("finishedOffsetPriceTableFill", () => {
					state.configuratorData.canSetConfigurationPrice = true;
				});
			}
		},
		SET_BOUNDING_RECT (state, payload) {
			state.configurator.boundaries = payload;
		},
		SET_VIEWPORT_INFO (state, payload) {
			let breakPoint = {
				s: 480,
				ms: 720,
				m: 1000,
				l: 1200
			};

			state.viewPort = {
				width: payload,
				s: payload <= breakPoint.s,
				ms: payload <= breakPoint.ms,
				m: payload <= breakPoint.m,
				l: payload <= breakPoint.l,
				xl: payload > breakPoint.l
			};
		},
		SET_SPECIFICATION_ID (state, payload) {
			state.baseProps = { ...state.baseProps, specificationId: payload };
		},
		SET_IS_SPECIFICATION_CONFIGURATION (state, payload) {
			state.baseProps = { ...state.baseProps, isSpecificationConfiguration: payload };
		},
		SET_PRESELECTED_QUANTITY (state, payload) {
			state.baseProps = { ...state.baseProps, preselectedQuantity: payload };
		}
	},
	actions: {
		_SetProductData () {
			const apiUrl = "configurator/get-product-front/";
			const frontIds = store.state.baseProps.productFrontId;
			let productData = [];

			if (store.state.baseProps.isConfiguration) {
				if (!store.state.configurator.steps.general.existingConfigurationAdded) {
					Vue.http
						.get(CONFIG.HOST + "configurator/api/get/" + store.state.baseProps.configId + "/")
						.then(async response => {
							const result = JSON.parse(response.bodyText);
							store.commit("SET_EXISTING_CONFIGURATION_DATA", result);

							await getFrontIdData(result.productFrontId);
						});
				}
			} else if (store.state.baseProps.isFront) {
				if (store.state.baseProps.specificationId) {
					Promise.all(frontIds.map(id => getFrontIdData(id)))
						.then(async () => preselectConfiguratorItems(frontIds, productData));
				} else {
					frontIds.forEach(getFrontIdData);
				}
			}

			async function getFrontIdData (frontId) {
				const result = await Vue.http.get(CONFIG.HOST + apiUrl + frontId + "/");

				productData.push(result.body);

				if (productData.length === frontIds.length) {
					store.commit("SET_PRODUCT_DATA", productData);
					store.commit("SET_CONFIGURATOR_PRODUCT_DATA");

					if (store.state.baseProps.isConfiguration) {
						store.commit("SET_EXISTING_CONFIGURATION_SELECTED");
					}
				}
			}
		},
		_getConfigurationPrice () {
			const isValidIfCustomSize = !store.state.selectedFormat.isCustomSize || store.state.customSizeValid;

			const hasValidConfiguration = store.getters.checkData.hasSelectedFormat
				&& store.getters.checkData.hasSelectedProduct
				&& store.getters.getConfiguratorData.canSetConfigurationPrice
				&& isValidIfCustomSize;

			if (hasValidConfiguration) {
				if (!store.state.orderLine.isOffset && store.getters.hasDigitalAvailable) {
					const payload = store.getters.getCalculationPayload;
					store.state.loading.price = true;

					clearTimeout(store.state.throttle.amount);

					store.state.throttle.amount = setTimeout(() => {
						payload.amount = store.state.orderLine.amount;

						Vue.http.post(CONFIG.HOST + "configurator/calculate-price-sdwn/", payload).then(result => {
							store.commit("SET_CONFIGURATION_PRICE", JSON.parse(result.bodyText));
							store.state.loading.price = false;
						});
					}, 300);
				}
			}
		},
		_getPriceTable () {
			const hasValidConfiguration = store.getters.checkData.hasSelectedFormat
				&& store.getters.checkData.hasSelectedProduct
				&& store.getters.isConfiguratorDataValid;

			if (hasValidConfiguration) {
				const payload = store.getters.getCalculationPayload;
				const priceTable = [];
				const amountData = store.getters.getAmountData;

				amountData.amountList.forEach(amount => {
					payload.amount = amount;
					const tableRow = {
						amount: amount,
						default: {},
						rush: {}
					};

					Vue.http.post(CONFIG.HOST + "configurator/calculate-price-sdwn/", payload).then(result => {
						const data = JSON.parse(result.bodyText);

						tableRow.default = data;
						tableRow.default.singlePrice = data.PriceWithoutTax / amount;
						tableRow.default.singlePriceWithTax = data.PriceWithTax / amount;

						if (store.getters.isRushOrderAvailable) {
							const rushPrice = data.PriceWithoutTax * store.state.selectedProduct.rushOrderCostPercentage + data.PriceWithoutTax;
							const rushPriceWithTax = data.PriceWithTax * store.state.selectedProduct.rushOrderCostPercentage + data.PriceWithTax;

							tableRow.rush.PriceWithoutTax = rushPrice;
							tableRow.rush.PriceWithTax = rushPriceWithTax;
							tableRow.rush.singlePrice = rushPrice / amount;
							tableRow.rush.singlePriceWithTax = rushPriceWithTax / amount;
						}
					});
					priceTable.push(tableRow);
				});

				store.commit("SET_PRICE_TABLE", priceTable);
			}
		},
		_getOffsetPriceTable () {
			return new Promise((resolve, reject) => {
				if (store.state.selectedFormat.offsetPricings.length > 0) {
					const relevantOptionIds = store.state.selectedOptions
						.map(option => option.baseOptionId)
						.filter(id => id != null);

					const availableOffsetPricings = store.state.selectedFormat.offsetPricings.find(pricings => {
						if (pricings.optionIds.every(id => relevantOptionIds.includes(id))) {
							return pricings;
						}
					});

					if (availableOffsetPricings) {
						store.commit("SET_OFFSET_SUPPORT", true);
						store.commit("SET_OFFSET_SKU_ID", availableOffsetPricings.id);
						const url = CONFIG.HOST + "configurator/get-offset-price-list/" + availableOffsetPricings.id + "/";
						Vue.http
							.get(url)
							.then(result => {
								const offsetPriceTableWithSinglePrice = result.body.offSetPrices.map(pricing => {
									pricing.Single = pricing.Price / pricing.Amount;
									return pricing;
								});

								store.commit("SET_OFFSET_PRICETABLE", offsetPriceTableWithSinglePrice);

								resolve();
							});
					} else {
						store.commit("SET_OFFSET_SUPPORT", false);
						resolve();
					}
				}
			});
		},
		_sendQuotationMail (context, data) {
			const hasValidConfiguration = store.getters.checkData.hasSelectedFormat
				&& store.getters.checkData.hasSelectedProduct
				&& store.getters.isConfiguratorDataValid;

			if (hasValidConfiguration) {
				let payload = store.getters.getPostConfig;

				Vue.http.post(CONFIG.HOST + "configurator/save-configuration/", payload).then(
					result =>
					{
						let quotationMailPayload = {
							configurationPublicId: result.data.configurationId,
							email: data.email,
							phoneNumber: data.phoneNumber,
							amount: data.amount,
							recaptchaToken: data.recaptchaToken
						};
						Vue.http.post(CONFIG.HOST + "quotation/quote-confirmation-submit/", null, {
							params: quotationMailPayload
						});
						store.commit("SET_QUOTATION_RESPONSE", result);
					},
					err => {
						store.commit("SET_QUOTATION_RESPONSE", err);
					}
				);
			}
		},
		_createConfiguration () {
			const existingPublicId = store.getters.getUploadSessionGuid;
			const selectedFormat = store.getters.getSelectedFormat;
			const specificationId = selectedFormat && selectedFormat.id ? selectedFormat.id : null;
			const selectedProduct = store.getters.getSelectedProduct;
			const productFrontId = selectedProduct ? selectedProduct.id : store.state.baseProps.productFrontId[0];
			const customSize = store.getters.getCustomSize;
			const pageCount = store.getters.getPageCount;

			const params = [];
			if (existingPublicId) {
				params.push(`publicId=${existingPublicId}`);
			}
			if (specificationId) {
				params.push(`specificationId=${specificationId}`);
			}
			if (productFrontId) {
				params.push(`productFrontId=${productFrontId}`);
			}
			if (customSize && customSize.width) {
				params.push(`customWidth=${customSize.width}`);
			}
			if (customSize && customSize.height) {
				params.push(`customHeight=${customSize.height}`);
			}
			if (pageCount) {
				params.push(`pageCount=${pageCount}`);
			}

			const url = CONFIG.HOST + "configurator/create-configuration/" + (params.length > 0 ? "?" + params.join("&") : "");

			return Vue.http.get(url).then(result => {
				store.commit("SET_UPLOAD_SESSION_GUID", result.body);
				return result;
			});
		},
		_saveConfiguration () {
			const hasValidConfiguration = store.getters.checkData.hasSelectedFormat
				&& store.getters.checkData.hasSelectedProduct
				&& store.getters.isConfiguratorDataValid;

			if (!hasValidConfiguration) {
				return Promise.resolve(null);
			}

			const payload = store.getters.getPostConfig;
			return Vue.http.post(CONFIG.HOST + "configurator/save-configuration", payload);
		},
		_postConfiguration () {
			const hasValidConfiguration = store.getters.checkData.hasSelectedFormat
				&& store.getters.checkData.hasSelectedProduct
				&& store.getters.isConfiguratorDataValid;

			if (hasValidConfiguration) {
				store.dispatch("_saveConfiguration").then(
					result => {
						if (result.data.statusCode !== 400) {
							Vue.http
								.get(CONFIG.HOST + "configurations/add-to-cart/", {
									params: {
										publicId: result.data.configurationId,
										isConfigurator: true
									}
								})
								.then(
									() => {
										store.commit("SET_ORDER", {
											success: true,
											message: "Het product is toegevoegd aan uw winkelwagen"
										});

										if (store.getters.getOrderLine.isRush) {
											Vue.http.post(CONFIG.HOST + "configurator/set-rush-order/", { isRushOrder: true });
										}

										try {
											ga("send", "event", "configurator", "toegevoegd aan winkelwagen");	// eslint-disable-line no-undef
										} finally {
											setTimeout(() => {
												window.location.href = CONFIG.HOST + "carts/step-one/";
											}, 500);
										}
									},
									error => {
										console.error(error);
										store.commit("SET_ORDER", {
											success: false,
											message: "Er is een fout opgetreden; het product toevoegen aan de winkelwagen is niet gelukt. [error:3]"
										});
									}
								);
						} else {
							console.error("Status 400");
							store.commit("SET_ORDER", {
								success: false,
								message: "De configuratie kan niet worden opgeslagen. [error:2]"
							});
						}
					},
					error => {
						console.error(error);
						store.commit("SET_ORDER", {
							success: false,
							message: "Kan geen verbinding tot stand brengen met de server. [error:1]"
						});
					}
				);
			}
		}
	}
});



// WEBPACK FOOTER //
// ./src/store/index.js